import { saveQueue } from './saveQueue';
import { savePartialProfileToFirebase, loadProfileFromFirebase, SaveData } from './firebaseStorage';
import { auth } from '../firebase';
import { db as dexieDb, SaveData as LocalSaveData } from '../storage/localAdapter';

class StorageManager {
  private currentProfileId: string | null = null;
  private currentProfileName: string | null = null;
  private saveStatus: 'idle' | 'pending' | 'saving' | 'saved' | 'error' = 'idle';
  private isInitialized = false;

  constructor() {
    // Set up save queue callbacks
    saveQueue.setSaveCallback(this.handleBatchedSave.bind(this));
    saveQueue.setStatusCallback(this.handleStatusChange.bind(this));
  }

  /**
   * Initialize the storage manager (call this on app start)
   */
  initialize() {
    if (this.isInitialized) return;

    // Force save when user leaves/closes tab
    window.addEventListener('beforeunload', (e) => {
      // Use synchronous save if possible, or queue for service worker
      saveQueue.forceSave();
    });

    // Force save when tab becomes hidden (user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        saveQueue.forceSave();
      }
    });

    // Periodic auto-save every 30 seconds (safety net)
    setInterval(() => {
      if (this.currentProfileId) {
        saveQueue.forceSave();
      }
    }, 30000);

    this.isInitialized = true;
    console.log('✅ Storage Manager initialized');
  }

  /**
   * Set the current active profile
   */
  setActiveProfile(profileId: string, profileName: string) {
    // Save any pending changes from previous profile
    if (this.currentProfileId && this.currentProfileId !== profileId) {
      saveQueue.forceSave();
    }

    this.currentProfileId = profileId;
    this.currentProfileName = profileName;
    console.log(`📋 Active profile set: ${profileName} (${profileId})`);
  }

  /**
   * Clear the active profile (call when logging out or closing profile)
   */
  clearActiveProfile() {
    if (this.currentProfileId) {
      saveQueue.forceSave();
    }
    this.currentProfileId = null;
    this.currentProfileName = null;
  }

  /**
   * Queue a change to warband data
   */
  saveWarbandChange(warband: any) {
    if (!this.currentProfileId) {
      console.warn('No active profile - skipping save');
      return;
    }
    saveQueue.queueChange('warband', warband);
    this.saveToLocalImmediate('warband', warband);
  }

  /**
   * Queue a change to models
   */
  saveModelsChange(models: any[]) {
    if (!this.currentProfileId) {
      console.warn('No active profile - skipping save');
      return;
    }
    saveQueue.queueChange('models', models);
    this.saveToLocalImmediate('models', models);
  }

  /**
   * Queue a change to items
   */
  saveItemsChange(items: any[]) {
    if (!this.currentProfileId) {
      console.warn('No active profile - skipping save');
      return;
    }
    saveQueue.queueChange('items', items);
    this.saveToLocalImmediate('items', items);
  }

  /**
   * Queue a change to locations
   */
  saveLocationsChange(locations: any[]) {
    if (!this.currentProfileId) {
      console.warn('No active profile - skipping save');
      return;
    }
    saveQueue.queueChange('locations', locations);
    this.saveToLocalImmediate('locations', locations);
  }

  /**
   * Queue a change to templates
   */
  saveTemplatesChange(modelTemplates: any[], itemTemplates: any[]) {
    if (!this.currentProfileId) {
      console.warn('No active profile - skipping save');
      return;
    }
    saveQueue.queueChange('templates', { modelTemplates, itemTemplates });
    this.saveToLocalImmediate('templates', { modelTemplates, itemTemplates });
  }

  /**
   * Save to IndexedDB immediately (fast, local, synchronous)
   */
  private async saveToLocalImmediate(type: string, data: any) {
    if (!this.currentProfileId) return;

    try {
      // Get current profile data
      const profile = await dexieDb.profiles.get(this.currentProfileId);

      if (!profile) {
        console.warn('Profile not found for local save');
        return;
      }

      // Update specific data type in the profile
      const updatedData = { ...profile.data };

      switch(type) {
        case 'warband':
          updatedData.warband = data;
          break;
        case 'models':
          updatedData.models = data;
          break;
        case 'items':
          updatedData.items = data;
          break;
        case 'locations':
          updatedData.locations = data;
          break;
        case 'templates':
          updatedData.modelTemplates = data.modelTemplates;
          updatedData.itemTemplates = data.itemTemplates;
          break;
      }

      // Save updated profile
      await dexieDb.profiles.update(this.currentProfileId, {
        data: updatedData,
        updatedAt: new Date(),
        lastAccessedAt: new Date()
      });

      console.log(`💾 Local save: ${type}`);
    } catch (error) {
      console.error('❌ Local save failed:', error);
      // Don't throw - local save failures shouldn't break the app
    }
  }

  /**
   * Handle batched save to Firebase (called by saveQueue)
   */
  private async handleBatchedSave(changes: Map<string, any>) {
    if (!this.currentProfileId || !this.currentProfileName) {
      console.warn('No active profile for cloud save');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      console.log('User not authenticated, skipping cloud save');
      return;
    }

    try {
      console.log(`☁️ Starting cloud save for: ${Array.from(changes.keys()).join(', ')}`);

      // Use partial save for efficiency (only saves what changed)
      await savePartialProfileToFirebase(
        user.uid,
        this.currentProfileId,
        changes
      );

      console.log('✅ Cloud save successful');
    } catch (error) {
      console.error('❌ Cloud save failed:', error);

      // Don't throw - we already saved locally
      // User will see "error" status but data is safe
    }
  }

  /**
   * Handle save status changes (for UI updates)
   */
  private handleStatusChange(status: 'idle' | 'pending' | 'saving' | 'saved' | 'error') {
    this.saveStatus = status;

    // Emit event for UI components to listen to
    window.dispatchEvent(new CustomEvent('saveStatusChange', { detail: status }));
  }

  /**
   * Get current save status
   */
  getSaveStatus() {
    return this.saveStatus;
  }

  /**
   * Load profile from storage (tries cloud first, falls back to local)
   */
  async loadProfile(profileId: string): Promise<SaveData | null> {
    const user = auth.currentUser;

    // Try cloud first if authenticated
    if (user) {
      try {
        console.log('☁️ Loading profile from cloud...');
        const cloudData = await loadProfileFromFirebase(user.uid, profileId);

        if (cloudData) {
          // Save to local as cache
          await dexieDb.profiles.put({
            id: profileId,
            profileName: this.currentProfileName || profileId,
            data: cloudData,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastAccessedAt: new Date()
          });

          console.log('✅ Profile loaded from cloud');
          return cloudData;
        }
      } catch (error) {
        console.error('❌ Cloud load failed, falling back to local:', error);
      }
    }

    // Fall back to local
    console.log('💾 Loading profile from local storage...');
    const profile = await dexieDb.profiles.get(profileId);
    return profile?.data || null;
  }

  /**
   * Force immediate save (use when closing menus, navigating away, etc.)
   */
  async forceSave() {
    console.log('💾 Force save triggered');
    await saveQueue.forceSave();
  }

  /**
   * Get debug info
   */
  getDebugInfo() {
    return {
      currentProfileId: this.currentProfileId,
      currentProfileName: this.currentProfileName,
      saveStatus: this.saveStatus,
      queueSize: saveQueue.getQueueSize(),
      isInitialized: this.isInitialized
    };
  }
}

// Export singleton
export const storageManager = new StorageManager();
